import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { faInfoCircle, faChevronRight, faUndo } from '@fortawesome/free-solid-svg-icons';
import {
  CanMapPlatformSignal,
  CanMapPlatformUcmdmBlock,
  SignalDiffResult,
} from 'src/app/pages/UControl/config/models/can-map-block.model';
import {
  SIGNAL_GAIN_MAX,
  SIGNAL_GAIN_MIN,
  SIGNAL_OFFSET_MAX,
  SIGNAL_OFFSET_MIN,
  TX_PERIOD_MS_MAX,
  TX_PERIOD_MS_MIN,
} from 'src/app/pages/UControl/config/constants/can-map-signal-templates.constants';

/**
 * Pannello con il nuovo blocco proposto, generato dalla configurazione UControl corrente.
 * Mostra un'unica anteprima JSON di sola lettura (tcu+platform) e, sotto, un'unica tabella
 * con tutti i campi modificabili dall'utente prima della conferma finale: tx_period_ms
 * (proprietà del blocco tcu, mostrato come prima riga della tabella) e gain/offset per
 * ogni segnale del blocco platform.
 */
@Component({
  selector: 'app-canmap-editable-panel',
  templateUrl: './canmap-editable-panel.component.html',
  styleUrls: ['./canmap-editable-panel.component.scss'],
})
export class CanmapEditablePanelComponent implements OnChanges {
  @Input() tcuBlock: any;
  @Input() platformBlock: CanMapPlatformUcmdmBlock;
  @Input() existingPlatformBlock: CanMapPlatformUcmdmBlock | null = null;
  /** null quando non c'è un blocco esistente da confrontare: nessuna evidenziazione da mostrare */
  @Input() signalDiff: SignalDiffResult | null = null;
  @Input() existingTxPeriodMs: number | null = null;

  editableSignals: CanMapPlatformSignal[] = [];
  editableTxPeriodMs: number;
  calculatedTxPeriodMs: number; // Valore precalcolato (per reset)

  readonly faInfoCircle = faInfoCircle;
  readonly faChevronRight = faChevronRight;
  readonly faUndo = faUndo;
  tableExpanded = false; // Tabella collassata di default
  readonly txPeriodMsMin = TX_PERIOD_MS_MIN;
  readonly txPeriodMsMax = TX_PERIOD_MS_MAX;
  readonly gainMin = SIGNAL_GAIN_MIN;
  readonly gainMax = SIGNAL_GAIN_MAX;
  readonly offsetMin = SIGNAL_OFFSET_MIN;
  readonly offsetMax = SIGNAL_OFFSET_MAX;

  ngOnChanges(changes: SimpleChanges): void {
    // Reagisce solo ai cambiamenti dei blocchi sorgente, non a `signalDiff` (che viene
    // ricalcolato dal parent ad ogni modifica dell'utente in questo stesso componente:
    // se ricostruissimo editableSignals/editableTxPeriodMs anche in quel caso, ogni
    // modifica dell'utente verrebbe immediatamente sovrascritta con i valori iniziali).
    const onlyDiffChanged =
      Object.keys(changes).every(key => key === 'signalDiff') && Object.keys(changes).length > 0;
    if (onlyDiffChanged) {
      return;
    }

    // Clone difensivo: non modifichiamo mai l'oggetto generato passato in input
    this.editableSignals = (this.platformBlock?.signals ?? []).map(s => {
      const clonedSignal = { ...s };
      // Se esiste un blocco precedente, usa i valori di gain/offset da lì, altrimenti mantieni i generati
      if (this.existingPlatformBlock?.signals) {
        const existingSignal = this.existingPlatformBlock.signals.find(es => es.par === s.par);
        if (existingSignal) {
          clonedSignal.gain = existingSignal.gain;
          clonedSignal.offset = existingSignal.offset;
        }
      }
      return clonedSignal;
    });

    // Salva il valore precalcolato per il reset
    this.calculatedTxPeriodMs = this.tcuBlock?.tx_period_ms;

    // Se esiste un valore precedente nella CAN Map, usalo; altrimenti usa il precalcolato
    this.editableTxPeriodMs = this.existingTxPeriodMs !== null ? this.existingTxPeriodMs : this.calculatedTxPeriodMs;
  }

  resetTxPeriodToCalculated(): void {
    this.editableTxPeriodMs = this.calculatedTxPeriodMs;
  }

  resetSignalGainToCalculated(signal: CanMapPlatformSignal): void {
    const generated = this.platformBlock?.signals.find(s => s.par === signal.par);
    if (generated) {
      signal.gain = generated.gain;
    }
  }

  resetSignalOffsetToCalculated(signal: CanMapPlatformSignal): void {
    const generated = this.platformBlock?.signals.find(s => s.par === signal.par);
    if (generated) {
      signal.offset = generated.offset;
    }
  }

  /** true quando il valore editato differe dal precalcolato: mostra il pulsante "reset" */
  isGainModified(signal: CanMapPlatformSignal): boolean {
    const generated = this.platformBlock?.signals.find(s => s.par === signal.par);
    return generated ? signal.gain !== generated.gain : false;
  }

  /** true quando il valore editato differe dal precalcolato: mostra il pulsante "reset" */
  isOffsetModified(signal: CanMapPlatformSignal): boolean {
    const generated = this.platformBlock?.signals.find(s => s.par === signal.par);
    return generated ? signal.offset !== generated.offset : false;
  }

  /**
   * true quando il valore editato differisce da quello attualmente salvato in CAN Map:
   * indica che il salvataggio produrrà un cambiamento reale (stessa semantica del badge
   * su tx_period_ms, basato su `signalDiff.txPeriodChanged`).
   */
  isSignalChangedFromExisting(signal: CanMapPlatformSignal): boolean {
    const existing = this.existingPlatformBlock?.signals?.find(es => es.par === signal.par);
    if (!existing) {
      return false; // segnale nuovo: nessun valore precedente con cui confrontare
    }
    return signal.gain !== existing.gain || signal.offset !== existing.offset;
  }

  getEditedPlatformBlock(): CanMapPlatformUcmdmBlock {
    return { ...this.platformBlock, signals: this.editableSignals };
  }

  getEditedTxPeriodMs(): number {
    return this.editableTxPeriodMs;
  }

  isTxPeriodValid(): boolean {
    return true;
  }

  isGainValid(signal: CanMapPlatformSignal): boolean {
    return true;
  }

  isOffsetValid(signal: CanMapPlatformSignal): boolean {
    return true;
  }

  isValid(): boolean {
    return true;
  }
}
